import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import LoginPage from '@cashew/page-login';
import NotAuthorised from '@cashew/page-not-authorized';
import NotFound from '@cashew/page-not-found';
import { storeUserData } from '../actions/user';
import Icon404 from '../assets/Icon-404-svg';
import LogoEN from '../assets/logo-en-svg';
import AbandonedPage from './Abandoned';
import CustomerDetailPage from './CustomerDetail';
import CustomersPage from './Customers';
import DispatchPage from './Dispatch';
import DispatchesPage from './Dispatches';
import DisputesPage from './Disputes';
import ForgotPasswordPage from './ForgotPassword';
import Home from './Home';
import MerchantOnboardingPage from './MerchantOnboarding';
import MerchantSettlementsPage from './MerchantSettlements';
import MerchantsPage from './Merchants';
import NeoLoanPage from './NeoLoan';
import NeoLoanOrderDetailPage from './NeoLoan/order-detail';
import NewPasswordPage from './NewPassword';
import OrderDetailPage from './Order-Detail';
import OrderDetailPlannedPurchasePage from './Order-Detail-planned-purchase';
import OrdersPage from './Orders';
import OutstandingPage from './Outstanding';
import PartnerDetails from './PartnerDetails';
import PartnerSettlementsPage from './PartnerSettlements';
import Partners from './Partners';
import PayNowPage from './PayNow';
import PlanPurchasePage from './PlanPurchase';
import RefundDetailPage from './RefundDetails';
import RefundsPage from './Refunds';
import RiskRules from './RiskRules';
import SettingsPage from './Settings';
import SettlementDetailPage from './Settlement-Detail';
import SubscriptionDetailPage from './SubscriptionDetail';
import SubscriptionHistoryPage from './SubscriptionHistory';
import SubscriptionsPage from './Subscriptions';
import TransactionsManagement from './TransactionsManagement';

const Secured = ({ authenticated, children, language }: any) =>
  authenticated ? children : <Redirect to={`/${language}/login`} />;

export default () => {
  const { authenticated, language, role } = useSelector(
    (state: any) => state.user
  );
  const { t } = useTranslation();
  const customerCare = role === 'CUSTOMER_CARE';
  return (
    <Switch>
      <Route path="/" exact>
        <Redirect to="/en" />
      </Route>
      <Route path="/:lang" exact>
        <Secured authenticated={authenticated} language={language}>
          {customerCare ? <OrdersPage /> : <Home />}
        </Secured>
      </Route>

      <Route path="/:lang/login" exact>
        <LoginPage t={t} storeUserData={storeUserData} type="cashew" />
      </Route>
      <Route path="/:lang/forgot-password" exact>
        <ForgotPasswordPage />
      </Route>
      <Route path="/:lang/reset-password" exact>
        <NewPasswordPage />
      </Route>

      <Route path="/:lang/:type(orders|pay-now|dispatches|disputes|abandoned)/:orderId">
        <Secured authenticated={authenticated} language={language}>
          <OrderDetailPage />
        </Secured>
      </Route>

      <Route path="/:lang/:type(planned-purchase)/:orderId">
        <Secured authenticated={authenticated} language={language}>
          <OrderDetailPlannedPurchasePage />
        </Secured>
      </Route>

      <Route path="/:lang/:type(neo-loan)/:orderId">
        <Secured authenticated={authenticated} language={language}>
          <NeoLoanOrderDetailPage />
        </Secured>
      </Route>

      <Route path="/:lang/partners/:financePartnerId">
        <Secured authenticated={authenticated} language={language}>
          <PartnerDetails />
        </Secured>
      </Route>

      {!customerCare && (
        <Route path="/:lang/dispatches">
          <Secured authenticated={authenticated} language={language}>
            <DispatchesPage />
          </Secured>
        </Route>
      )}
      <Route path="/:lang/orders/:orderId/dispatch">
        <Secured authenticated={authenticated} language={language}>
          <DispatchPage />
        </Secured>
      </Route>
      <Route path="/:lang/orders/:orderId/transactions-management">
        <Secured authenticated={authenticated} language={language}>
          <TransactionsManagement />
        </Secured>
      </Route>

      <Route path="/:lang/pay-now">
        <Secured authenticated={authenticated} language={language}>
          <PayNowPage />
        </Secured>
      </Route>

      <Route path="/:lang/orders">
        <Secured authenticated={authenticated} language={language}>
          <OrdersPage />
        </Secured>
      </Route>

      <Route path="/:lang/planned-purchase">
        <Secured authenticated={authenticated} language={language}>
          <PlanPurchasePage />
        </Secured>
      </Route>

      <Route path="/:lang/neo-loan">
        <Secured authenticated={authenticated} language={language}>
          <NeoLoanPage />
        </Secured>
      </Route>

      <Route path="/:lang/partners">
        <Secured authenticated={authenticated} language={language}>
          <Partners />
        </Secured>
      </Route>

      <Route
        path="/:lang(en|ar)/subscriptions/:orderId(1001\d{10})/history"
        exact
      >
        <Secured authenticated={authenticated} language={language}>
          <SubscriptionHistoryPage />
        </Secured>
      </Route>
      <Route path="/:lang(en|ar)/subscriptions/:orderId(1001\d{10})" exact>
        <Secured authenticated={authenticated} language={language}>
          <SubscriptionDetailPage />
        </Secured>
      </Route>
      <Route path="/:lang(en|ar)/subscriptions" exact>
        <Secured authenticated={authenticated} language={language}>
          <SubscriptionsPage />
        </Secured>
      </Route>
      {!customerCare && (
        <Route path="/:lang/refunds/:refundId">
          <Secured authenticated={authenticated} language={language}>
            <RefundDetailPage />
          </Secured>
        </Route>
      )}
      {!customerCare && (
        <Route path="/:lang/refunds">
          <Secured authenticated={authenticated} language={language}>
            <RefundsPage />
          </Secured>
        </Route>
      )}

      <Route path="/:lang/customers/:customerId">
        <Secured authenticated={authenticated} language={language}>
          <CustomerDetailPage />
        </Secured>
      </Route>
      <Route path="/:lang/customers">
        <Secured authenticated={authenticated} language={language}>
          <CustomersPage />
        </Secured>
      </Route>

      {!customerCare && (
        <Route path="/:lang/merchants/:merchantId">
          <Secured authenticated={authenticated} language={language}>
            <MerchantOnboardingPage />
          </Secured>
        </Route>
      )}
      {!customerCare && (
        <Route path="/:lang/merchants">
          <Secured authenticated={authenticated} language={language}>
            <MerchantsPage />
          </Secured>
        </Route>
      )}

      {!customerCare && (
        <Route path="/:lang/settlements/:settlementId">
          <Secured authenticated={authenticated} language={language}>
            <SettlementDetailPage />
          </Secured>
        </Route>
      )}
      {!customerCare && (
        <Route path="/:lang/merchant-settlements/:selectedMerchant?/:selectedStartDate?/:selectedEndDate?">
          <Secured authenticated={authenticated} language={language}>
            <MerchantSettlementsPage />
          </Secured>
        </Route>
      )}
      {!customerCare && (
        <Route path="/:lang/partner-settlements">
          <Secured authenticated={authenticated} language={language}>
            <PartnerSettlementsPage />
          </Secured>
        </Route>
      )}
      {!customerCare && (
        <Route path="/:lang/outstandings">
          <Secured authenticated={authenticated} language={language}>
            <OutstandingPage />
          </Secured>
        </Route>
      )}
      {!customerCare && (
        <Route path="/:lang/disputes">
          <Secured authenticated={authenticated} language={language}>
            <DisputesPage />
          </Secured>
        </Route>
      )}
      <Route path="/:lang/abandoned">
        <Secured authenticated={authenticated} language={language}>
          <AbandonedPage />
        </Secured>
      </Route>

      {!customerCare && (
        <>
          <Route path="/:lang(en|ar)/settings" exact>
            <SettingsPage />
          </Route>
          <Route path="/:lang(en|ar)/risk-rules" exact>
            <RiskRules />
          </Route>
        </>
      )}
      <Route path="/:lang/401">
        <NotAuthorised t={t} Icon404={Icon404} LogoEN={LogoEN} />
      </Route>
      <Route path="/">
        <NotFound t={t} Icon404={Icon404} LogoEN={LogoEN} />
      </Route>
    </Switch>
  );
};
